import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { AlertTriangle } from 'lucide-react'
import PivotTable, { type PivotColumn } from '@/components/analytics/explorer/PivotTable'
import {
  type DealFact,
  type PersonProfile,
  type RelationshipRow,
} from '@/services/dashboardAnalytics'

type RelationshipKind = 'brokerages' | 'brokers' | 'sponsors'

const KIND_LABEL: Record<RelationshipKind, string> = {
  brokerages: 'Brokerage',
  brokers: 'Broker',
  sponsors: 'Sponsor',
}

const COLUMNS: PivotColumn[] = [
  { key: 'count' },
  { key: 'commitment' },
  { key: 'fundedCount' },
  { key: 'funded', className: 'hidden min-[1800px]:table-cell' },
  { key: 'quoteRatePct' },
]

function shortDate(value: string | null | undefined): string {
  if (!value) return '--'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return '--'
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

interface RelationshipTableProps {
  relationships: PersonProfile['relationships']
  deals: readonly DealFact[]
  personName: string
  quietDays?: number | null
}

/**
 * Which relationships feed the book: the person's brokerages, brokers and
 * sponsors by deal flow — deals, volume, funded, quote rate and the last dated
 * deal event. "At risk" appears only with its derived reason (T-67); no
 * hand-entered last contact is read (T-74).
 */
export default function RelationshipTable({ relationships, deals, personName, quietDays }: RelationshipTableProps) {
  const [kind, setKind] = useState<RelationshipKind>('brokerages')
  const facts = useMemo(() => new Map(deals.map((deal) => [deal.id, deal])), [deals])
  const rows = (relationships?.[kind] ?? []) as RelationshipRow[]
  const flagged = rows.filter((row) => row.atRisk).length

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-amber-400/[0.06] px-2 py-1.5">
        {(Object.keys(KIND_LABEL) as RelationshipKind[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setKind(key)}
            aria-pressed={kind === key}
            className={clsx(
              'rounded-full border px-2 py-0.5 text-[10px] transition-colors',
              kind === key ? 'border-amber-400/35 bg-amber-400/[0.08] text-amber-200' : 'border-amber-400/10 text-stone-500 hover:text-stone-300',
            )}
          >
            {KIND_LABEL[key]}s · {(relationships?.[key] ?? []).filter((row) => row.key != null).length}
          </button>
        ))}
        {flagged > 0 && (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-amber-300/90">
            <AlertTriangle size={10} aria-hidden="true" /> {flagged} gone quiet
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="px-3 py-4 text-[11px] text-stone-500">No {KIND_LABEL[kind].toLowerCase()} is named on {personName}'s deals.</p>
      ) : (
        <PivotTable
          rows={rows}
          totals={null}
          dimensionLabel={KIND_LABEL[kind]}
          by={null}
          facts={facts}
          scopeLabel={personName}
          columns={COLUMNS}
          className="min-h-0 flex-1"
          extraColumns={[
            {
              key: 'last',
              label: 'Last deal',
              render: (row) => {
                const rel = row as RelationshipRow
                return (
                  <span className="inline-flex items-center gap-1">
                    {rel.atRisk && (
                      <span title={rel.atRiskReason ?? undefined} className="text-amber-300" aria-label={rel.atRiskReason ?? 'At risk'}>
                        <AlertTriangle size={9} />
                      </span>
                    )}
                    {shortDate(rel.lastDealDate)}
                  </span>
                )
              },
            },
          ]}
        />
      )}
      <p className="flex-shrink-0 border-t border-amber-400/[0.06] px-2 py-1 text-[9.5px] text-stone-500">
        From deal flow, not CRM last contact (T-74).
        {quietDays ? ` A relationship with 2+ deals and no dated deal event in ${quietDays} days is marked gone quiet, with the reason on hover (threshold pending sign-off).` : ''}
      </p>
    </div>
  )
}
